import * as React from 'react'

import {
  ALL_STUDENTS_OPTION,
  DEFAULT_MOMENTUM_SCORE_GOAL,
  MAX_VIEW_WIDTH_FOR_SEGMENTED_CONTROL,
} from './helpers'

import { requestGet, } from '../../../modules/request'
import useSnackbarMonitor from '../../Shared/hooks/useSnackbarMonitor'
import { defaultSnackbarTimeout, } from '../../Shared/index'
import { DropdownObjectInterface } from '../../Staff/interfaces/evidenceInterfaces'

export const usePusherMessage = (pusherChannel, eventKey: string) => {
  const [pusherMessage, setPusherMessage] = React.useState<string | null>(null)

  React.useEffect(() => {
    pusherChannel?.bind(eventKey, (body) => { setPusherMessage(body?.message ?? null) })
  }, [pusherChannel])

  return pusherMessage
}

export const useMomentumScoreGoal = (storageKey: string) => {
  const [momentumScoreGoal, setMomentumScoreGoal] = React.useState(() => {
    const storedGoal = window.localStorage.getItem(storageKey)
    return storedGoal ? Number(storedGoal) : DEFAULT_MOMENTUM_SCORE_GOAL
  })

  React.useEffect(() => {
    window.localStorage.setItem(storageKey, String(momentumScoreGoal))
  }, [momentumScoreGoal])

  return [momentumScoreGoal, setMomentumScoreGoal] as const
}

// The teacher selection that was in effect the last time the user clicked
// Apply (tracked via the parent's searchCount). The student filter only
// unlocks when exactly one specific teacher has been applied.
export const useAppliedTeacher = (selectedTeachers, availableTeachers, searchCount: number) => {
  const [appliedTeachers, setAppliedTeachers] = React.useState(selectedTeachers)

  React.useEffect(() => { setAppliedTeachers(selectedTeachers) }, [searchCount])

  const singleTeacherApplied = appliedTeachers && appliedTeachers.length === 1 && availableTeachers && appliedTeachers.length < availableTeachers.length
  // Keyed on the teacher's id so re-applying the same teacher doesn't refetch.
  const appliedTeacherId = singleTeacherApplied ? appliedTeachers[0].id : null

  return { appliedTeacherId, singleTeacherApplied }
}

export const useTeacherStudents = (appliedTeacherId: number | null, studentsEndpoint: string) => {
  const [studentFilterValue, setStudentFilterValue] = React.useState<DropdownObjectInterface>(ALL_STUDENTS_OPTION)
  const [studentOptions, setStudentOptions] = React.useState<DropdownObjectInterface[]>([ALL_STUDENTS_OPTION])
  const [studentsLoading, setStudentsLoading] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (!appliedTeacherId) {
      setStudentOptions([ALL_STUDENTS_OPTION])
      setStudentFilterValue(ALL_STUDENTS_OPTION)
      setStudentsLoading(false)
      return
    }
    setStudentsLoading(true)
    requestGet(`${studentsEndpoint}/${appliedTeacherId}`, (body) => {
      if (Array.isArray(body)) {
        setStudentOptions([ALL_STUDENTS_OPTION, ...body.map((s) => ({ label: s.name, value: String(s.id) }))])
      }
      setStudentsLoading(false)
    })
    setStudentFilterValue(ALL_STUDENTS_OPTION)
  }, [appliedTeacherId])

  return { studentFilterValue, setStudentFilterValue, studentOptions, studentsLoading }
}

export const useSnackbar = () => {
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarText, setSnackbarText] = React.useState('')

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  const displaySnackbar = (text: string) => {
    setSnackbarText(text)
    setShowSnackbar(true)
  }

  return { showSnackbar, snackbarText, displaySnackbar }
}

// jsdom has no matchMedia; default to the wide layout there.
export const useViewToggleFitsSegmentedControl = () => {
  const query = `(min-width: ${MAX_VIEW_WIDTH_FOR_SEGMENTED_CONTROL}px)`
  const [fits, setFits] = React.useState(() => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : true))

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return

    const mediaQuery = window.matchMedia(query)
    const handleBreakpointChange = (e: MediaQueryListEvent) => setFits(e.matches)
    mediaQuery.addEventListener('change', handleBreakpointChange)
    return () => mediaQuery.removeEventListener('change', handleBreakpointChange)
  }, [])

  return fits
}
